import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('atlas_token'));

  const axiosInstance = axios.create({
    baseURL: '',
    withCredentials: false,
  });

  // Add token to requests
  axiosInstance.interceptors.request.use((config) => {
    const storedToken = localStorage.getItem('atlas_token');
    if (storedToken) {
      config.headers.Authorization = `Bearer ${storedToken}`;
    }
    return config;
  });

  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem('atlas_token');
    if (!storedToken) {
      setLoading(false);
      return;
    }
    try {
      const response = await axiosInstance.get('/api/auth/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('atlas_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axiosInstance.post('/api/auth/login', { email, password });
    const { token: newToken, ...userData } = response.data;
    localStorage.setItem('atlas_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, business_context, language = 'fr') => {
    const response = await axiosInstance.post('/api/auth/register', {
      name,
      email,
      password,
      business_context,
      language,
    });
    const { token: newToken, ...userData } = response.data;
    localStorage.setItem('atlas_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axiosInstance.post('/api/auth/logout');
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('atlas_token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (data) => {
    const response = await axiosInstance.put('/api/auth/me', data);
    setUser(prev => ({ ...prev, ...response.data }));
    return response.data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      token,
      login,
      register,
      logout,
      updateProfile,
      isAuthenticated: !!user,
      api: axiosInstance,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
